import { Link } from 'react-router'
import {
  ChainRail,
  PrimaryCta,
  Reveal,
  Section,
  SectionHeading,
  SecondaryCta,
  Seo,
  ShotFrame,
} from '@/components/primitives'
import { MotionVideo } from '@/components/MotionVideo'

const OPERATING_CHAIN = [
  'Where work comes from',
  'Customer records',
  'Rules and routing',
  'Queues and teams',
  'Calls and workflows',
  'Outcomes',
  'Reports and improvement',
]

const SYSTEMS = [
  {
    name: 'Customer context',
    scope: 'One record per customer, with the whole story.',
    objects: ['Contacts', 'Sources', 'Statuses', 'Notes', 'Touchpoints', 'Pipeline', 'Outcomes'],
  },
  {
    name: 'Movement and orchestration',
    scope: 'The right work to the right person.',
    objects: ['Workflows', 'Routing', 'Hoppers', 'Queues', 'Skills', 'Tasks', 'Callbacks', 'Approvals'],
  },
  {
    name: 'Communications',
    scope: 'Every call and text on the record.',
    objects: ['Calls', 'Messages', 'Participants', 'Events', 'Recordings', 'Transcription workflows', 'Dispositions'],
  },
  {
    name: 'People and capacity',
    scope: 'Match your team to the demand.',
    objects: ['Users', 'Roles', 'Teams and groups', 'Schedules', 'Presence', 'Skills', 'Licensing context'],
  },
  {
    name: 'Completion',
    scope: 'Forms, signatures, and policies, done.',
    objects: ['Forms', 'Documents', 'Templates', 'Signatures', 'Policies', 'Sales', 'Referrals'],
  },
  {
    name: 'Intelligence and evidence',
    scope: 'Numbers you can click through.',
    objects: ['Dashboards', 'Reports', 'Attribution', 'Logs', 'Nex', 'APIs'],
  },
]

const GOVERNANCE = [
  'Account scope on every record and action',
  'Roles and field masking for every user',
  'Approvals and versions on every workflow',
  'Audit and export logs on demand',
]

const EXTENSIBILITY = [
  {
    name: 'Integrations',
    detail: 'Connect telephony, messaging, quoting, and document providers. Available with the right providers connected.',
  },
  {
    name: 'APIs and schema discovery',
    detail: 'Scoped keys against a schema-driven core. Outside tools see the same objects you do.',
  },
  {
    name: 'Events, webhooks, and relays',
    detail: 'Webhooks bring events in. Relays send events out to your other systems.',
  },
  {
    name: 'Workflow actions',
    detail: 'External calls run as governed workflow steps: permission-checked, versioned, and logged.',
  },
]

export default function Platform() {
  return (
    <>
      <Seo
        title="Platform | TLDNXS Customer Operations Platform"
        description="One platform for the whole operating chain. Over 145,000,000 policies processed on the TLD stack. Book a walkthrough."
      />

      {/* Thesis */}
      <section aria-labelledby="platform-title" className="relative overflow-hidden">
        <MotionVideo
          src="/videos/control-plane-web.mp4"
          poster="/videos/control-plane-poster.jpg"
          className="pointer-events-none absolute inset-0 opacity-25"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-base-950/70 via-base-950/55 to-base-950"
        />
        <div
          aria-hidden="true"
          className="field-grid pointer-events-none absolute inset-0 opacity-30 [mask-image:radial-gradient(70%_60%_at_50%_20%,black,transparent)]"
        />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-20 text-center sm:px-8 md:py-28">
          <Reveal>
            <p className="eyebrow">Customer Operations Platform</p>
            <h1
              id="platform-title"
              className="mx-auto mt-5 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl"
            >
              One platform for the whole operating chain.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink-mute md:text-lg">
              CRMs file contacts. TLDNXS runs the work: where it comes from, who owns it, what
              happened, and what should happen next.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <PrimaryCta to="/walkthrough">Book a walkthrough</PrimaryCta>
              <SecondaryCta to="/solutions">See solutions</SecondaryCta>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mx-auto mt-14 max-w-4xl">
              <p className="mono-label mb-4">The operating chain</p>
              <div className="flex justify-center">
                <ChainRail items={OPERATING_CHAIN} ariaLabel="Operating chain from demand to improvement" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Six capability systems */}
      <Section id="capability-systems" labelledBy="systems-heading" className="bg-base-900/40">
        <Reveal>
          <SectionHeading
            id="systems-heading"
            title="Six systems. One chain. Zero swivel-chair."
            copy="Each system is deep enough to run its job. They all share the same records, permissions, and audit trail."
          />
        </Reveal>
        <Reveal delay={100}>
          <ol className="mt-12 grid gap-px overflow-hidden rounded-sm border border-base-line bg-base-line md:grid-cols-2">
            {SYSTEMS.map((system, i) => (
              <li key={system.name} className="bg-base-850 p-6">
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-[11px] tabular-nums text-ink-dim">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-lg font-extrabold tracking-tight text-ink">{system.name}</p>
                </div>
                <p className="mt-2 text-sm text-ink-mute">{system.scope}</p>
                <ul className="mt-4 flex flex-wrap gap-1.5" aria-label={`${system.name} objects`}>
                  {system.objects.map((object) => (
                    <li key={object} className="chip-neutral normal-case tracking-normal">
                      {object}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </Reveal>
      </Section>

      {/* Configurability */}
      <Section labelledBy="config-heading">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                id="config-heading"
                title="Configure the operation around how you actually work."
                copy="Fields, layouts, rules, workflows, and views match how you work. Not the other way around."
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <ul className="space-y-4">
                {[
                  'Define the fields and layouts each record carries.',
                  'Route work by state, license, skill, and capacity.',
                  'Version every workflow through draft, review, and publish.',
                  'Shape each role’s view without splitting the data.',
                ].map((line) => (
                  <li
                    key={line}
                    className="border-l-2 border-signal/50 pl-4 text-sm leading-relaxed text-ink-mute"
                  >
                    {line}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-ink-dim">
                TLDNXS is configured around your operation. A walkthrough is the fastest way to
                define your scope.
              </p>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Real product surfaces, both themes */}
      <Section labelledBy="surfaces-heading" className="bg-base-900/40">
        <Reveal>
          <SectionHeading
            id="surfaces-heading"
            title="Dark or light. Same power."
            copy="Pick the theme your team prefers. Records, rules, and history stay identical."
          />
        </Reveal>
        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          <Reveal delay={80}>
            <ShotFrame
              src="/images/tldnxs-appt-dark-1100w.webp"
              srcSet="/images/tldnxs-appt-dark-768w.webp 768w, /images/tldnxs-appt-dark-1100w.webp 1100w, /images/tldnxs-appt-dark-1600w.webp 1600w"
              sizes="(min-width: 1024px) 44vw, 100vw"
              width={1100}
              height={631}
              alt="TLDNXS appointments view in the dark theme with schedule, contact details, and call actions"
              caption="Appointments · dark theme"
            />
          </Reveal>
          <Reveal delay={140}>
            <ShotFrame
              src="/images/tldnxs-appt-light-1100w.webp"
              srcSet="/images/tldnxs-appt-light-768w.webp 768w, /images/tldnxs-appt-light-1100w.webp 1100w, /images/tldnxs-appt-light-1600w.webp 1600w"
              sizes="(min-width: 1024px) 44vw, 100vw"
              width={1100}
              height={631}
              alt="TLDNXS appointments view in the light theme with the same schedule and contact context"
              caption="Appointments · light theme"
            />
          </Reveal>
        </div>
      </Section>

      {/* Nex band */}
      <Section labelledBy="platform-nex-heading">
        <div className="grid items-center gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="eyebrow-nex">Nex · intelligence layer</p>
              <h2
                id="platform-nex-heading"
                className="mt-4 text-3xl font-extrabold tracking-tight text-ink"
              >
                An assistant that knows your setup.
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink-mute">
                Nex answers questions and drafts changes inside your permissions. You review. You
                publish.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Reveal delay={100}>
              <Link to="/nex" className="btn-secondary">
                Explore Nex
              </Link>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Governance */}
      <Section labelledBy="gov-heading" className="bg-base-900/40">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                id="gov-heading"
                title="Control is the product, not an add-on."
                copy="Every change happens inside roles, permissions, and logs."
              />
              <Link to="/security" className="btn-ghost mt-4 px-0">
                Read the security overview →
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <ul className="grid gap-3 sm:grid-cols-2">
                {GOVERNANCE.map((item) => (
                  <li
                    key={item}
                    className="rounded-sm border border-base-line bg-base-850 px-4 py-3.5 text-sm text-ink-mute"
                  >
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-ink-dim">
                Controls vary by deployment and account policy.
              </p>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Extensibility */}
      <Section id="extensibility" labelledBy="ext-heading">
        <Reveal>
          <SectionHeading
            id="ext-heading"
            title="Your stack plugs in. The operation stays one."
            copy="APIs, webhooks, and integrations attach outside systems to the same records and logs."
          />
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-12 grid gap-px overflow-hidden rounded-sm border border-base-line bg-base-line md:grid-cols-2">
            {EXTENSIBILITY.map((item) => (
              <div key={item.name} className="bg-base-850 p-6">
                <p className="text-base font-extrabold tracking-tight text-ink">{item.name}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink-mute">{item.detail}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-ink-dim">
            Available with the right providers connected.{' '}
            <Link to="/integrations" className="text-signal hover:text-signal-soft">
              Browse the integration catalog
            </Link>
            .
          </p>
        </Reveal>
      </Section>

      {/* CTA */}
      <Section labelledBy="platform-cta-heading" className="bg-base-900/40">
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            <h2
              id="platform-cta-heading"
              className="text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
            >
              See TLDNXS on your operation.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink-mute md:text-lg">
              Bring your sources, teams, and goals. We will map them live.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <PrimaryCta to="/walkthrough">Book a walkthrough</PrimaryCta>
              <SecondaryCta to="/">Back to the homepage</SecondaryCta>
            </div>
          </div>
        </Reveal>
      </Section>
    </>
  )
}
